import { SelectableRow } from "@/design-system/SelectableRow/SelectableRow";
import { positionToChain } from "@/functions/getFormation";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import { MetaRating } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import {
  Focus,
  PlayerRole,
  getRolesPlusOrPlusPlus,
  getTier,
} from "../../../../../redux/players/playersSlice";
import { findMetaRating } from "../../../../../redux/squadBuilder/findMetaRating";
import { SquadBuilderPlayerType } from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { PlayerFocus } from "../../components/PlayerRole/PlayerFocus/PlayerFocus";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  player: SquadBuilderPlayerType;
  handleClickedClose: () => void;
  handleRoleChanged: (
    playerRoleId: number,
    metaRatings: MetaRating[],
    focus: string,
  ) => void;
  changingPitchItemOnIndex: number;
  availablePlayerRoles: Record<string, boolean>;
  playerRoles: PlayerRole[];
  chemistry?: number;
  selectedPlayerRoleId?: number;
  selectedFocus?: string;
  handlePositionAndRoleChanged?: (
    position: string,
    roleId: number,
    metaRatings: MetaRating[],
  ) => void;
  isBench: boolean;
  position: string;
  uniquePosition?: string;
};

export const positionsInSections = Object.entries(positionToChain).reduce(
  (acc, [key, value]) => {
    if (value === "attackers") {
      acc.attackers.push(key);
    } else if (value === "midfielders") {
      acc.midfielders.push(key);
    } else if (value === "defenders") {
      acc.defenders.push(key);
    } else if (value === "goalkeepers") {
      acc.goalkeepers.push(key);
    }
    return acc;
  },
  { attackers: [], midfielders: [], defenders: [], goalkeepers: [] },
);

// Each group with its heading's key
const POSITION_GROUPS = [
  ["attackers", "playerRole.attackers"],
  ["midfielders", "playerRole.midfielders"],
  ["defenders", "playerRole.defenders"],
  ["goalkeepers", "playerRole.goalkeeper"],
] as const;

const Positions = ({
  selectedPosition,
  handleSelectPosition,
}: {
  selectedPosition: string;
  handleSelectPosition: (position: string) => void;
}) => {
  const { t } = useTranslation("squadBuilder");
  const { positions } = useSelector(contentLabelsSelector);

  return (
    <ComponentContainer title={t("playerRole.position")} marginTop="mt-8">
      <div className="flex flex-col gap-y-4">
        {POSITION_GROUPS.map(([group, titleKey]) => (
          <div key={group} className="flex flex-col">
            <p className="text-xs">{t(titleKey)}</p>
            <div className="flex flex-wrap gap-1">
              {positionsInSections[group].map((position) => (
                <SelectableRow
                  key={position}
                  variant="chip"
                  label={positions[position]?.name ?? position}
                  selected={selectedPosition === position}
                  onSelect={() => handleSelectPosition(position)}
                  className="w-[78px] sb-v2-lg:w-[80px]"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </ComponentContainer>
  );
};

export const PlayerRoleMode = ({
  player,
  handleClickedClose,
  handleRoleChanged,
  playerRoles,
  chemistry,
  position,
  uniquePosition,
  selectedFocus: selectedFocusId,
  selectedPlayerRoleId,
  handlePositionAndRoleChanged,
  isBench,
}: Props) => {
  const ref = useOutsideClick(() => handleClickedClose());
  const { t } = useTranslation("squadBuilder");
  const [selectedPlayerRole, setSelectedPlayerRole] = useState<PlayerRole>();
  const [selectedFocus, setSelectedFocus] = useState<Focus>();
  const findPlayerRoleForPosition = (position: string) => {
    return playerRoles.filter((playerRole) => playerRole.position === position);
  };

  const handleSelectPositionForBench = (position: string) => {
    const playerRole = findPlayerRoleForPosition(position);
    setSelectedPlayerRole(playerRole[0]);
    handlePositionAndRoleChanged(
      position,
      playerRole[0].id,
      player?.metaRatings || [],
    );
  };

  useEffect(() => {
    const localSelectedPlayerRole = playerRoles.find(
      (playerRole) =>
        playerRole.id === selectedPlayerRoleId ||
        playerRole.id === player?.playerRoleId,
    );
    setSelectedPlayerRole(localSelectedPlayerRole);

    setSelectedFocus(
      localSelectedPlayerRole?.focuses.find(
        (focus) => focus.name === selectedFocusId,
      ) || localSelectedPlayerRole?.focuses[0],
    );
  }, [selectedPlayerRoleId, selectedFocusId, playerRoles]);

  const selectPlayerRoleOrFocus = ({
    playerRoleId,
    focus,
  }: {
    playerRoleId?: number;
    focus?: string;
  }) => {
    if (isBench) {
      // TODO: change this
      handlePositionAndRoleChanged(
        player?.position,
        playerRoleId,
        player?.metaRatings || [],
      );
      const playerRole = playerRoles.find((pr) => pr.id === playerRoleId);
      setSelectedPlayerRole(playerRole);
    } else {
      handleRoleChanged(playerRoleId, player?.metaRatings || [], focus);
    }
  };

  let focuses = selectedPlayerRole?.focuses?.filter((focus) =>
    focus.positions.includes(position),
  );

  return (
    <div ref={ref}>
      <PanelHeader
        title={
          isBench ? t("panel.playerRoleAndPosition") : t("panel.addPlayerRole")
        }
        onClose={handleClickedClose}
      />
      <PanelContainer>
        {isBench && (
          <Positions
            selectedPosition={player?.position}
            handleSelectPosition={handleSelectPositionForBench}
          />
        )}
        <ComponentContainer
          marginTop="mt-8"
          title={
            <div className="flex flex-col whitespace-nowrap gap-x-1">
              <span>{t("playerRole.title")}</span>
              <span className="text-xs text-gray-200">
                {t("playerRole.impactsAi")}
              </span>
            </div>
          }
        >
          <div className="grid items-center w-full grid-cols-1 gap-1">
            {playerRoles
              .filter((pr) => pr.position === position)
              .map((pr) => {
                const metaRatingAt = (chem: number) =>
                  findMetaRating({
                    metaRatings: player?.metaRatings ?? [],
                    playerRoleId: pr.id,
                    chemistry: chem,
                  })?.metaRating;
                // Tier comes from 0 chem, as on the cards
                const metaRatingZeroChem = metaRatingAt(0);
                return (
                  <SelectableRow
                    key={pr.name}
                    label={pr.name}
                    plus={getRolesPlusOrPlusPlus({
                      playerRoleId: pr.id,
                      rolesPlus: player.rolesPlus,
                    })}
                    rating={metaRatingAt(chemistry)?.toFixed(1)}
                    tier={
                      metaRatingZeroChem
                        ? getTier(playerRoles, metaRatingZeroChem, pr.id, true)
                            ?.tierName
                        : undefined
                    }
                    selected={selectedPlayerRole?.id === pr.id}
                    onSelect={() =>
                      selectPlayerRoleOrFocus({ playerRoleId: pr.id })
                    }
                  />
                );
              })}
          </div>
          <div className="min-h-[120px]">
            <p className="pt-3 text-xs text-white">
              {t("playerRole.roleDescription")}
            </p>
            <p className="pt-1 text-xs text-gray-300">
              {selectedPlayerRole?.description}
            </p>
          </div>
        </ComponentContainer>

        {!isBench && (
          <ComponentContainer marginTop="mt-3" title={t("playerRole.focus")}>
            <div className="grid items-center w-full grid-cols-3 desktop:grid-cols-2 gap-[10px] sb-v2-lg:gap-4">
              {focuses?.map((focus, index) => (
                <div
                  key={focus.name}
                  className={cn(
                    focuses.length === index + 1 &&
                      index % 2 === 0 &&
                      "desktop:col-span-2 desktop:mx-auto",
                  )}
                >
                  <PlayerFocus
                    heatmapValues={
                      (uniquePosition && focus.heatmaps?.[uniquePosition]) ||
                      focus.heatmaps?.[position] ||
                      []
                    }
                    focus={focus.name}
                    active={focus.name === selectedFocus?.name}
                    onClick={() => {
                      selectPlayerRoleOrFocus({
                        playerRoleId: selectedPlayerRole?.id,
                        focus: focus.name,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="min-h-[120px]">
              <p className="pt-3 text-xs text-white">
                {t("playerRole.focusDescription")}
              </p>
              <p className="pt-1 text-xs text-gray-300">
                {selectedFocus?.description}
              </p>
            </div>
          </ComponentContainer>
        )}
      </PanelContainer>
    </div>
  );
};
